<template>
  <div v-if="canRead">
    <div class="card col-md-12">
      <div class="card-header" style="margin-bottom: 10px">
        <h4 class="card-title" style="display:inline">
          Community Search
        </h4>
        <button type="button" v-if="canEdit" @click="add" class="btn btn-fill btn-info btn-add-community pull-right">
          Add Community
        </button>
      </div>
      <div class="card-body form-group row">
        <div class="col-md-11">
          <label>&nbsp;</label>
          <el-input type="search" name="search" placeholder="Search" clearable v-validate="'required'" @keyup.enter.native="search" v-model="query" />
          <small class="text-danger" v-show="errors.has('search')">
            {{ getError('search') }}
          </small>
        </div>
        <div class="col-md-1" style="padding-left: 0;">
          <label>Active</label>
          <p-switch v-model="active" type="primary" on-text="YES" off-text="NO" style="display:block"></p-switch>
        </div>
        <div class="col-md-12">
          <button type="button" @click="search" :disabled="isSearchButtonDisabled" class="btn btn-fill btn-info pull-right btn-search">
            Search
          </button>
          <button type="button" @click="clear" class="btn btn-fill btn-info pull-right btn-search">
            Clear
          </button>
        </div>
      </div>
    </div><!-- end card -->
    <div class="card">
      <div class="card-body">
        <div class="col-md-12">
          <el-table class="wrap-whole-words indexed-table-column-width"
                    :data="tableData"
                    style="width: 100%"
                    :max-height="tableHeight(300)">
            <el-table-column
              :width="this.constants.INDEX_COLUMN_WIDTHS.WIDE"
              align="center"
              label="#"
              type="index">
            </el-table-column>
            <el-table-column label="Community">
              <template slot-scope="props">
                <span class="invisible">{{ props.row.id }}</span>
                <a href="#" @click.prevent="edit(props.row.id)">{{ props.row.name }}</a>
              </template>
            </el-table-column>
            <el-table-column label="Clients">
              <template slot-scope="props">
                <ul>
                  <li v-for="client in getClients(props.row.clientIds)">{{ client }}</li>
                </ul>
              </template>
            </el-table-column>
            <el-table-column label="Builders">
              <template slot-scope="props">
                <ul>
                  <li v-for="builder in getBuilders(props.row.builderIds)">{{ builder }}</li>
                </ul>
              </template>
            </el-table-column>
            <el-table-column
              width="105"
              fixed="right"
              label="Actions">
              <template slot-scope="props">
                <p-button type="success" size="sm" icon>
                  <i class="fa fa-edit" @click="edit(props.row.id)"></i>
                </p-button>
                <p-button type="danger" v-if="canEdit" size="sm" icon @click="confirmDelete(props.row)">
                  <i class="fa fa-times"></i>
                </p-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import {mapGetters} from 'vuex'
  import {notifySuccess, notifyWarning, notifyError, messageForSafeDelete, getTableHeight} from 'src/utilities/utils'
  import _ from 'lodash'
  import PSwitch from 'src/components/UIComponents/Switch.vue'
  import PButton from 'src/components/UIComponents/Button.vue'
  import Permissions from 'src/utilities/permissions'
  import {OK, NO_CONTENT, UNAUTHORIZED} from 'http-status-codes'
  import Constants from 'src/utilities/constants'

  export default {
    components: {
      PButton,
      PSwitch
    },
    computed: {
      ...mapGetters({
        tableData: 'communities/results',
        clients: 'companies/clientMap',
        builders: 'companies/builderMap'
      }),
      isSearchButtonDisabled() {
        return !this.query
      },
      canRead() {
        return this.$can('read', Permissions.COMMUNITIES)
      },
      canEdit() {
        return this.$can('edit', Permissions.COMMUNITIES)
      }
    },
    data() {
      return {
        toDelete: null,
        query: null,
        active: true,
        constants: Constants
      }
    },
    methods: {
      getClients(ids) {
        const clientNames = []
        if (ids) {
          ids.forEach(id => {
            if (this.clients[id]) {
              clientNames.push(this.clients[id].name)
            }
          })
        }
        return clientNames
      },
      getBuilders(ids) {
        const builderNames = []
        if (ids) {
          ids.forEach(id => {
            if (this.builders[id]) {
              builderNames.push(this.builders[id].name)
            }
          })
        }
        return builderNames
      },
      clear() {
        this.$validator.pause()
        this.query = null
        this.$store.commit('communities/resetResults')
        setTimeout(this.resumeValidation, 250)
      },
      resumeValidation() {
        this.$validator.resume()
      },
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      edit(communityId) {
        this.$eventHub.$emit('show-modal-1', 'Community', {id: communityId}, '500px')
      },
      tableHeight(buffer) {
        return getTableHeight(buffer)
      },
      async search(optionalCommunity) {
        let isSearching = true // refreshing search results
        let query = _.trim(this.query)
        if (optionalCommunity.hasOwnProperty('name')) {
          query = optionalCommunity.name
          isSearching = false
        }

        // don't fire validation when reloading after adding/editing a community name
        if (!isSearching || (isSearching && await this.$validator.validateAll())) {
          const response = await this.$store.dispatch('communities/fetchByName', {
            query: query,
            active: this.active
          })

          if (OK !== response.status) {
            notifyError(this, 'Error Searching Communities')
          } else if (this.query && response.data.length === 0) {
            this.$eventHub.$emit('show-modal-1', 'No Results Found', null, '500px')
          }
        }
      },
      async confirmDelete(community) {
        this.toDelete = community.id
        let showButton = false

        const responseJobs = await this.$store.dispatch('jobs/fetchExistsByCommunityId', community.id)

        if (OK !== responseJobs.status) {
          notifyError(this, 'Failed to fetch Jobs or see if ' + community.name + ' is in use. Unable to delete community.')
          return
        }
        if (responseJobs.data === true){
          const message = 'You can not delete ' + community.name + ' as it is used by at least one job. You may inactivate it.'
          this.showConfirmDelete(community, message, showButton)
          return
        }

        const responsePriceRules = await this.$store.dispatch('priceRules/fetchFindByCommunityId', community.id)

        if (OK !== responsePriceRules.status) {
          notifyError(this, 'Failed to fetch Price Rules. Unable to safely delete community.')
          return
        }

        let message = 'Are you sure you want to delete <strong>' + community.name + '</strong>? This will completely remove the community.'
        const usedByPriceRules = responsePriceRules.data
        if (usedByPriceRules.length > 0) {
          message = '<strong>' + community.name + '</strong> is used by the following price rules and cannot be deleted:'
          message += '<p>'
          message = messageForSafeDelete(usedByPriceRules, 'shortId', message)
          message += '</p>'
          message += '<p>If you really want to delete this community, you must first remove associations to it in each of the price rules listed above, then come back here and delete the community.</p>'
          showButton = false
          this.showConfirmDelete(community, message, showButton)
          return
        }

        // happy path - community can be deleted
        showButton = true
        this.showConfirmDelete(community, message, showButton)
      },
      showConfirmDelete(community, message, showButton) {
        const payload = {
          message,
          entity: community,
          delete: this.delete,
          showButton
        }
        this.$eventHub.$emit('show-modal-1', 'Confirm Delete', payload, '600px')
      },
      async delete() {
        const response = await this.$store.dispatch('delete', {
          endpoint: 'communities',
          id: this.toDelete
        })
        if (NO_CONTENT === response.status) {
          this.$store.commit('communities/delete', this.toDelete)
          notifySuccess(this, 'Community Deleted Successfully')
        } else if (UNAUTHORIZED === response.status) {
          notifyWarning(this, 'You are not authorized to delete Communities')
        } else {
          notifyError(this, 'Error Deleting Community')
        }
      },
      add() {
        this.$eventHub.$emit('show-modal-1', 'Community', null, '500px')
      }
    },
    mounted() {
      this.$eventHub.$on('communitySaved', this.search)
    },
    destroyed() {
      this.$eventHub.$off('communitySaved')
    }
  }
</script>
<style>
  .btn-add-community {
    margin-left: 15px;
  }
  .btn-search {
    margin-bottom: 15px;
   }
</style>
